import { useEffect, useContext, FC, useState, MouseEvent, useCallback, useMemo, useRef } from 'react';
import { debounce, isEmpty, isNil, orderBy, some } from 'lodash';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBinoculars, faTrashCan, faPlus, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faClose, faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  Box,
  Button,
  IconButton,
  Input,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  PaperProps,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Search } from '@material-ui/icons';
import {
  CreateNewDialog,
  TGSelectFieldWeb,
  TGSelectProps,
  TGTextFieldWeb,
  useFormDialog,
  useUserInfo,
} from '@terragotech/gen5-shared-components';
import { colors } from '../../../../styles/theme';
import { hasIncludes, MOBILE_BREAKPOINT } from '../../../../utils/utilityHelper';
import { useAlert } from '../../../../contexts/AlertModalContext';
import { AssetsDashboardContext } from '../../../../contexts/assetsDashboardContext';
import { LanguageContext } from '../../../../contexts/LanguageContext/languageContext';
import { SavedViewResponse } from '../../../../graphql';
import {
  SaveCurrentViewParams,
  SavedViewsResponseItem,
  useSavedViews,
} from '../../../../hooks/useSavedViews';
import { useInfoContext } from '../../../../contexts/InfoContext/infoContext';
import { useOptions } from '../../../Workflow/FormHelpers';
import useRoles from '../../../../hooks/useRoles';
import { useCommonStyles } from './VisibilityButton/useStyles';

const POPOVER_WIDTH_WEB_VIEW = 312;
const POPOVER_HEIGHT_WEB_VIEW = 318;
const PopupHeader = ({ handleClose, title }: { title: string; handleClose: () => void }) => {
  const classes = useStyles();
  const { translate } = useContext(LanguageContext);
  return (
    <div className={`${classes.popoverHeader} ${classes.mapTypePopOverHeader}`}>
      <Typography className={classes.mapHeader}>{translate(title)}</Typography>
      <IconButton onClick={handleClose} className={classes.closeRoot}>
        <FontAwesomeIcon icon={faClose as IconProp} className={classes.closeIcon} />
      </IconButton>
    </div>
  );
};

interface ViewstateComponentProps {}
export const ViewstateComponent: FC<ViewstateComponentProps> = () => {
  const classes = useStyles();

  const { isMobileView } = useContext(AssetsDashboardContext);
  const { hasAiAnalysisAccess } = useUserInfo();
  const { openConfirmation } = useAlert();
  const { openSnackbar } = useInfoContext();
  const formDialog = useFormDialog();
  const { getSavedViewDetail, loadSavedView, resetCurrentView, removeSavedView, saveCurrentView, savedViews } = useSavedViews();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const paperMenuProps: PaperProps = {
    style: {
      width: POPOVER_WIDTH_WEB_VIEW,
      minHeight: POPOVER_HEIGHT_WEB_VIEW,
    },
  };

  const onSavedViewAdd = useCallback(async () => {
    // TODO: Add callback option for submit action prior to closing modal (i.e. error handling).
    const newView = await formDialog<typeof CreateAddSavedViewModal>(
      props => <CreateAddSavedViewModal {...props} showAiFilterNotice={hasAiAnalysisAccess} />,
      true,
      true,
      680,
      { paper: classes.dialog },
    );
    try {
      await saveCurrentView(newView);
      openSnackbar({ type: 'SUCCESS', title: `View '${newView.name}' has been successfully saved.` });
    } catch (e: any) {
      openSnackbar({
        type: 'ERROR',
        title: `View '${newView.name}' could not be saved.`,
      });
    }
  }, [saveCurrentView]);

  const onSavedViewLoad = useCallback(
    async (savedView: SavedViewsResponseItem) => {
      const status = await openConfirmation({
        type: 'confirmation',
        title: `Apply Custom View`,
        question: `The following view will be applied. Do you want to continue?`,
        description: <SavedViewDetail savedView={savedView} />,
        cancellationText: 'Cancel',
        confirmationText: 'Apply',
      });
      if (status === 'confirm') {
        try {
          await loadSavedView(savedView.id);
          openSnackbar({ type: 'SUCCESS', title: `View '${savedView.name}' has been successfully applied.` });
        } catch (e: any) {
          openSnackbar({
            type: 'ERROR',
            title: `View '${savedView.name}' (ID: '${savedView.id}') could not be applied.`,
          });
        }
      }
    },
    [openConfirmation, loadSavedView]
  );

  const onSavedViewDelete = useCallback(
    async (savedView: SavedViewsResponseItem) => {
      const status = await openConfirmation({
        type: 'error',
        title: `Delete Custom View`,
        question: `The following view will be deleted. Do you want to continue?`,
        description: (
          <>
            <SavedViewDetail savedView={savedView} />
            <Typography className={classes.actionWarningText}>WARNING: This action cannot be undone.</Typography>
          </>
        ),
        cancellationText: 'Cancel',
        confirmationText: 'DELETE',
        dialogContainerStyle: {},
      });
      if (status === 'confirm') {
        try {
          await removeSavedView(savedView.id);
          openSnackbar({ type: 'SUCCESS', title: `View '${savedView.name}' has been successfully deleted.` });
        } catch (e: any) {
          openSnackbar({
            type: 'ERROR',
            title: `View '${savedView.name}' (ID: '${savedView.id}') could not be deleted.`,
          });
        }
      }
    },
    [openConfirmation, removeSavedView]
  );

  const onSavedViewReset = useCallback(async () => {
    const status = await openConfirmation({
      type: 'confirmation',
      title: `Reset Current View`,
      question: `The view will be reverted to system default. Do you want to continue?`,
      cancellationText: 'Cancel',
      confirmationText: 'Reset',
    });
    if (status === 'confirm') {
      try {
        await resetCurrentView();
        openSnackbar({ type: 'SUCCESS', title: `View has been successfully reset.` });
      } catch (e: any) {
        openSnackbar({ type: 'ERROR', title: `View could not be reset.` });
      }
    }
  }, [openConfirmation, resetCurrentView]);

  const onSavedViewShowDetail = useCallback(async (savedView: SavedViewsResponseItem, showFullDetails: boolean) => {
    await openConfirmation({
      type: 'confirmation',
      title: `Custom View Detail`,
      description: <SavedViewDetail savedView={showFullDetails ? (await getSavedViewDetail(savedView)) : savedView} />,
      cancellationText: 'Close',
      showSubmit: false,
      dialogContainerStyle: {},
    });
  }, [saveCurrentView]);

  useEffect(() => {
    const touchHandler = (ev: Event, preventDefault: boolean = true) => {
      // @ts-ignore
      ev?.target?.click?.();
      if (preventDefault) ev?.preventDefault();
    };

    const domElements = Array.from(
      document.getElementsByClassName(classes.mapCenterControls)?.[0]?.getElementsByTagName('button') ?? []
    );
    for (const el of domElements) {
      el.addEventListener('touchend', touchHandler);
    }

    return () => {
      domElements.forEach(el => {
        el.removeEventListener('touchend', touchHandler);
      });
    };
  }, []);

  return (
    <>
      <div className={classes.mapCenterControls}>
        <div aria-label="Saved Views" className={[classes.icon, classes.icon2].join(' ')}>
          <IconButton
            disableRipple
            className={[classes.IconBut, !!anchorEl && classes.active].join(' ')}
            onClick={handleClick}>
            <FontAwesomeIcon icon={faBinoculars as IconProp} className={classes.iconSize} />
          </IconButton>
        </div>
      </div>
      {anchorEl && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={paperMenuProps}
          classes={{ paper: isMobileView ? [classes.menuRoot, classes.menuRootMob].join(' ') : classes.menuRoot }}>
          <>
            <Box className={classes.popupHeader}>
              <PopupHeader handleClose={handleClose} title={'Custom Views'} />
            </Box>
            <SavedViewList
              savedViews={savedViews}
              showSearchBar={true}
              onAddSavedView={onSavedViewAdd}
              onDefaultViewLoad={onSavedViewReset}
              onSavedViewLoad={onSavedViewLoad}
              onSavedViewDelete={onSavedViewDelete}
              onSavedViewShowDetail={onSavedViewShowDetail}
            />
          </>
        </Popover>
      )}
    </>
  );
};

interface SavedViewListProps {
  savedViews?: SavedViewsResponseItem[];
  showSearchBar?: boolean;
  onAddSavedView?: () => Promise<void>;
  onDefaultViewLoad?: () => Promise<void>;
  onSavedViewLoad?: (savedView: SavedViewsResponseItem) => Promise<void>;
  onSavedViewDelete?: (savedView: SavedViewsResponseItem) => Promise<void>;
  onSavedViewShowDetail?: (savedView: SavedViewsResponseItem, showFullDetails: boolean) => Promise<void>;
}
export const SavedViewList: FC<SavedViewListProps> = ({
  onAddSavedView,
  onDefaultViewLoad,
  onSavedViewDelete,
  onSavedViewLoad,
  onSavedViewShowDetail,
  savedViews,
  showSearchBar,
}) => {
  const mapOverlayCommonClasses = useCommonStyles();
  const classes = useStyles();
  const { translate } = useContext(LanguageContext);
  const { username } = useUserInfo();

  const [searchText, setSearchText] = useState('');
  const [canFocusOut, setCanFocusOut] = useState(false);

  const defaultView = useMemo<SavedViewsResponseItem>(
    () => ({
      id: 'defaultSystemView',
      name: 'Default System View',
      createdBy: '',
      createdByUser: '',
      createdDateTime: new Date().toISOString(),
    }),
    []
  );

  const includeDefaultView = useMemo(() => !isNil(onDefaultViewLoad), [onDefaultViewLoad]);

  const sortedViews = useMemo(() => orderBy(
    savedViews ?? [],
    [x => x.createdBy === username, x => x.createdDateTime],
    ['desc', 'desc'],
  ), [savedViews]);

  const allViews = useMemo(() => includeDefaultView ? [defaultView, ...sortedViews] : sortedViews, [includeDefaultView, defaultView, sortedViews]);

  const filteredViews = useMemo(
    () => allViews.filter(({ name }) => some([hasIncludes(name, searchText)])),
    [allViews, searchText]
  );

  const onDetailContainerScroll = useCallback(() => setCanFocusOut(true), [setCanFocusOut]);

  return (
    <Box className={mapOverlayCommonClasses.container}>
      {showSearchBar && (
        <SavedViewListSearch focusOut={canFocusOut} onSearchChange={setSearchText} searchValue={searchText} />
      )}
      {onAddSavedView && (
        <Box className={classes.headerButtonGroup}>
          <Button className={classes.savedViewAddButton} onClick={onAddSavedView}>
            <FontAwesomeIcon icon={faPlus as IconProp} className={classes.iconSize} />
            <Typography className={classes.toggleTxt}>{translate('Save View')}</Typography>
          </Button>
        </Box>
      )}
      <Box style={{ minHeight: '450px', maxHeight: '450px', overflowY: 'auto' }}>
        <Box
          className={`${mapOverlayCommonClasses.detailContainer} ${classes.detailContainer}`}
          onScroll={showSearchBar ? onDetailContainerScroll : undefined}
        >
          {isEmpty(filteredViews) ? (
            <>
              <Typography className={classes.emptyText}>{translate('No Custom Views')}</Typography>
            </>
          ) : (
            <div>
              {filteredViews.map(sv => (
                <SavedViewListRow
                  key={sv.id}
                  savedView={sv}
                  {...(sv.id === defaultView.id ? {
                    onSavedViewLoad: onDefaultViewLoad,
                  } : {
                    onSavedViewLoad,
                    onSavedViewDelete,
                    onSavedViewShowDetail,
                  })}
                />
              ))}
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );
};

interface SavedViewListSearchProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  focusOut: boolean;
}
export const SavedViewListSearch: FC<SavedViewListSearchProps> = ({ focusOut, onSearchChange, searchValue }) => {
  const classes = useStyles();
  const { translate } = useContext(LanguageContext);

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (focusOut) {
      inputRef.current?.blur();
    }
  }, [focusOut]);

  return (
    <>
      <Box className={classes.searchContainer}>
        <Input
          className={classes.searchbar}
          inputRef={inputRef}
          startAdornment={
            <InputAdornment position="start">
              <Search className={classes.searchIcon} />
            </InputAdornment>
          }
          disableUnderline
          placeholder={translate('Search')}
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onSearchChange(e.target.value);
          }}
        />
      </Box>
    </>
  );
};

interface SavedViewListRowProps {
  savedView: SavedViewsResponseItem;
  onSavedViewLoad?: (savedView: SavedViewsResponseItem) => Promise<void>;
  onSavedViewDelete?: (savedView: SavedViewsResponseItem) => Promise<void>;
  onSavedViewShowDetail?: (savedView: SavedViewsResponseItem, showFullDetails: boolean) => Promise<void>;
}
export const SavedViewListRow: FC<SavedViewListRowProps> = ({ savedView, onSavedViewDelete, onSavedViewLoad, onSavedViewShowDetail }) => {
  const classes = useStyles();
  const { translate } = useContext(LanguageContext);
  const { username } = useUserInfo();

  const [actionsPopoverAnchorEl, setActionsPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);

  const paperMenuProps = useMemo<Partial<PaperProps>>(
    () => ({
      style: {
        width: 151,
        minHeight: 48,
      },
    }),
    []
  );

  const isSavedViewOwner = useMemo(() => savedView.createdBy === username, [savedView.createdBy, username]);
  const savedViewSecondaryLabel = useMemo(() => !isSavedViewOwner && savedView.createdByUser || undefined, [isSavedViewOwner, savedView.createdByUser]);

  const onSavedViewActionsButtonClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      setActionsPopoverAnchorEl(e.currentTarget);
    },
    [setActionsPopoverAnchorEl]
  );

  const onActionsPopoverClose = useCallback(() => {
    setActionsPopoverAnchorEl(null);
  }, [setActionsPopoverAnchorEl]);

  const onSavedViewLoadButtonClick = useCallback(() => {
    onSavedViewLoad?.(savedView);
  }, [onSavedViewLoad, savedView]);

  const onSavedViewDeleteButtonClick = useCallback(() => {
    onSavedViewDelete?.(savedView).then(onActionsPopoverClose);
  }, [onSavedViewDelete, savedView]);

  const onSavedViewShowDetailButtonClick = useCallback(() => {
    onSavedViewShowDetail?.(savedView, isSavedViewOwner);
  }, [onSavedViewShowDetail, savedView, isSavedViewOwner]);

  return (
    <>
      <Box className={classes.rowContainer}>
        <Tooltip title={!isSavedViewOwner && savedView.createdByUser ? `Shared from '${savedView.createdByUser}'` : ''}>
          <Button
            className={classes.savedViewActionsButton}
            onClick={onSavedViewActionsButtonClick}
            disabled={!isSavedViewOwner}>
            <FontAwesomeIcon icon={faEllipsisVertical as IconProp} className={classes.iconSize} />
          </Button>
        </Tooltip>
        <Menu
          open={!!actionsPopoverAnchorEl}
          anchorEl={actionsPopoverAnchorEl}
          getContentAnchorEl={null}
          onClose={onActionsPopoverClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={paperMenuProps}
          classes={{ paper: classes.actionsMenuRoot }}>
          {onSavedViewDelete && (
            <MenuItem className={classes.savedViewDeleteButton} onClick={onSavedViewDeleteButtonClick}>
              <FontAwesomeIcon
                icon={faTrashCan as IconProp}
                className={`${classes.iconSize} ${classes.savedViewDeleteButtonIcon}`}
              />
              <Typography className={classes.savedViewDeleteButtonText}>{translate('Delete')}</Typography>
            </MenuItem>
          )}
        </Menu>
        <ListItemText
          primary={savedView.name}
          secondary={savedViewSecondaryLabel}
          className={classes.rowLabelContainer}
          classes={{ primary: classes.rowLabelPrimary, secondary: classes.rowLabelSecondary }}
        />
        <Button
          className={classes.savedViewShowDetailButton}
          onClick={onSavedViewShowDetailButtonClick}
          disabled={!onSavedViewShowDetail}
          hidden={!onSavedViewShowDetail}
        >
          <FontAwesomeIcon icon={faInfoCircle as IconProp} className={classes.iconSize} />
        </Button>
        <Button className={classes.savedViewLoadButton} onClick={onSavedViewLoadButtonClick}>
          <Typography className={classes.toggleTxt}>{translate('Apply')}</Typography>
        </Button>
      </Box>
    </>
  );
};

interface SavedViewDetailProps {
  savedView: Partial<SavedViewResponse>;
};
const SavedViewDetail: FC<SavedViewDetailProps> = ({ savedView }): JSX.Element => {
  const { createdByUser, createdDateTime, id, name, appVersion, roles, users } = savedView;
  const { translate } = useContext(LanguageContext);

  const appVersionLabel = useMemo(() => appVersion && `v${appVersion}`, [createdDateTime]);
  const createdDateTimeLabel = useMemo(() => createdDateTime && new Date(createdDateTime).toString(), [createdDateTime]);
  // const viewstateLabel = useMemo(() => viewstate && Object.keys(viewstate).join(', '), [viewstate]);
  const rolesLabel = useMemo(() => roles?.map(x => x.role).join(', '), [roles]);
  const usersLabel = useMemo(() => users?.map(x => x.user).join(', '), [users]);

  return (
    <>
      <ListItemText primary={translate('ID')} secondary={id} hidden={!id} />
      <ListItemText primary={translate('Title')} secondary={name} hidden={!name} />
      <ListItemText primary={translate('App Version')} secondary={appVersionLabel} hidden={!appVersionLabel} />
      <ListItemText primary={translate('Created By')} secondary={createdByUser} hidden={!createdByUser} />
      <ListItemText primary={translate('Created Date')} secondary={createdDateTimeLabel} hidden={!createdDateTimeLabel} />
      <ListItemText primary={translate('Shared with Roles')} secondary={rolesLabel} hidden={!rolesLabel} />
      <ListItemText primary={translate('Shared with Users')} secondary={usersLabel} hidden={!usersLabel} />
      {/* <ListItemText primary='Viewstate' secondary={viewstateLabel} hidden={!viewstateLabel} /> */}
    </>
  );
};

interface CreateAddSavedViewModalProps {
  onClose: () => void;
  onSubmit: (props: SaveCurrentViewParams) => void;
  showAiFilterNotice?: boolean;
}
export const CreateAddSavedViewModal: FC<CreateAddSavedViewModalProps> = ({ onClose, onSubmit, showAiFilterNotice }) => {
  const classes = useStyles();
  const { translate } = useContext(LanguageContext);
  const { rolesSelect } = useRoles();
  const userOptions = useOptions({ type: 'UserLoader' });

  const [nameFieldError, setNameFieldError] = useState<string>();
  const [name, setName] = useState<string>('');
  const [roleIds, setRoleIds] = useState<number[]>();
  const [userIds, setUserIds] = useState<string[]>();

  const isSubmittingRef = useRef(false);

  const rolesOptions = useMemo<TGSelectProps['options']>(
    () => rolesSelect?.map(r => ({ value: r.id, label: r.name })),
    [rolesSelect]
  );

  const validate = useCallback(({ name }: SaveCurrentViewParams) => {
    setNameFieldError(undefined);

    let isValid = true;
    if (isEmpty(name)) {
      setNameFieldError('Title is required.');
      isValid = false;
    } else if (name.length > 255) {
      setNameFieldError('Title cannot be more than 255 characters long.');
      isValid = false;
    }

    return isValid;
  }, [setNameFieldError]);

  const onDialogClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onDialogSubmit = useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    const newView: SaveCurrentViewParams = { name, roleIds, userIds };
    if (validate(newView)) {
      onSubmit(newView);
    }

    isSubmittingRef.current = false;
  }, [onSubmit, name, roleIds, userIds]);

  const onNameChange = useCallback(
    debounce(
      e => {
        setName(e.target.value);
        validate({ name: e.target.value });
      },
      200,
      { leading: false, trailing: true }
    ),
    [setName]
  );

  const onRolesChange = useCallback(
    debounce(
      newRoleIds => {
        setRoleIds(newRoleIds.map((id: string) => Number(id)));
      },
      200,
      { leading: false, trailing: true }
    ),
    [setRoleIds]
  );

  const onUsersChange = useCallback(
    debounce(
      newUserIds => {
        setUserIds(newUserIds);
      },
      200,
      { leading: false, trailing: true }
    ),
    [setUserIds]
  );

  // TODO: Control styles: Can we use `ControlProvider` with inline `controlDefinition`?
  return (
    <CreateNewDialog
      title="Save View"
      cancelText="Cancel"
      submitText="Submit"
      onClose={onDialogClose}
      onSubmit={onDialogSubmit}
      dialogClasses={{
        wrapper: classes.dialogWrapper,
        header: classes.dialogHeader,
        content: classes.dialogContent,
        button: classes.dialogButton,
        closeButton: classes.dialogCloseButton,
        cancelButton: classes.dialogCancelButton,
        submitButton: classes.dialogSubmitButton,
      }}>
      <Box className={`${classes.fieldWrapper} ${nameFieldError ? classes.fieldWrapperError : ''}`}>
        <TGTextFieldWeb
          label="Title"
          placeholder="Enter Title"
          defaultValue={name}
          onChange={onNameChange}
          required={true}
          error={!!nameFieldError}
          helperText={nameFieldError}
          translateFunction={translate}
        />
      </Box>
      <Box className={classes.fieldWrapper}>
        <TGSelectFieldWeb
          {...userOptions}
          label="Share with Users"
          placeholder="Placeholder"
          onChange={onUsersChange}
          multiSelect={true}
          required={false}
          translateFunction={translate}
          value={userIds}
        />
      </Box>
      <Box className={classes.fieldWrapper}>
        <TGSelectFieldWeb
          options={rolesOptions}
          label="Share with Roles"
          placeholder="Placeholder"
          onChange={onRolesChange}
          multiSelect={true}
          required={false}
          translateFunction={translate}
          value={roleIds}
        />
      </Box>
      {showAiFilterNotice && (
        <Typography className={classes.actionInfoText}>
          Note: Custom views do not include AI filters; record sets may differ if an AI filter is active when applying a custom view.
        </Typography>
      )}
    </CreateNewDialog>
  );
};

const useStyles = makeStyles(theme => ({
  mapCenterControls: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    marginRight: 0,
    backgroundColor: colors.white,
    borderRadius: 5,
  },
  IconBut: {
    '&:hover': {
      backgroundColor: `${colors.black05} !important`,
    },
  },
  iconSize: {
    fontSize: 20,
  },
  popoverHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    paddingTop: 17,
    paddingLeft: 24,
    paddingRight: 14,
  },
  popupHeader: {
    paddingBottom: 10,
  },
  mapTypePopOverHeader: {
    paddingTop: 22,
    paddingLeft: 24,
    paddingRight: 16,
  },
  mapHeader: {
    color: colors.black0,
    fontSize: 16,
    fontWeight: 500,
    fontStyle: 'normal',
    lineHeight: 'normal',
  },
  closeRoot: {
    width: 24,
    height: 24,
    position: 'relative',
    top: -2.5,
  },
  closeIcon: {
    fontSize: 20,
    color: colors.black54,
    cursor: 'pointer',
  },
  icon: {
    backgroundColor: colors.ghostWhite,
    opacity: '0.95',
    transition: 'background-color 0.16s ease-out 0s',
    cursor: 'pointer',
    borderWidth: '0px 0px 0px',
    borderTopStyle: 'initial',
    borderRightStyle: 'initial',
    borderBottomStyle: 'none',
    borderLeftStyle: 'initial',
    borderTopColor: 'initial',
    borderRightColor: 'initial',
    borderLeftColor: 'initial',
    borderImage: 'initial',
    height: '34px !important',
    width: '34px !important',
    backgroundPosition: '0px 0px',
    backgroundSize: '26px 260px',
    outline: 0,
    color: colors.black54,
  },
  icon2: {
    background: colors.white,
    borderRadius: 5,
    boxShadow: `0px 2px 4px 0px ${colors.black15} !important`,
    border: 'none !important',
    marginLeft: 0,
    pointerEvents: 'auto',
    right: '10px !important',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    zIndex: 12,
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      '& > button': {
        height: '26px !important',
        width: '26px !important',
        '& svg': {
          pointerEvents: 'none',
        },
      },
    },
  },
  menuRoot: {
    marginLeft: -7,
    borderRadius: 5,
    background: colors.white,
    boxShadow: `0px 4px 4px 0px ${colors.black15}`,
    transform: 'translate(9px, 5px) !important',
    minHeight: '50px !important',
  },
  menuRootMob: {
    transform: 'none !important',
  },
  actionsMenuRoot: {
    transform: 'none !important',
  },
  active: {
    color: theme.palette.primary.main,
  },
  savedViewAddButton: {
    display: 'flex',
    padding: '12px 10px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderRadius: 5,
    background: colors.white,
    boxShadow: `0 1px 6px 0 ${colors.black10}`,
    color: theme.palette.primary.main,
    textTransform: 'none',
    '& > .MuiButton-label': {
      gap: 10,
    },
    '&:hover': {
      color: `${colors.white} !important`,
      backgroundColor: theme.palette.primary.dark + ' !important',
    },
  },
  savedViewActionsButton: {
    minWidth: 26,
    color: colors.black54,
    '&.Mui-disabled': {
      color: colors.black10,
    },
    alignSelf: 'flex-start',
  },
  savedViewLoadButton: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderRadius: 30,
    '&:hover': {
      color: `${colors.white} !important`,
      backgroundColor: theme.palette.primary.dark + ' !important',
    },
    alignSelf: 'flex-start',
    flex: '0 0 auto',
  },
  savedViewShowDetailButton: {
    minWidth: 32,
    width: 32,
    color: colors.info,
    borderRadius: 30,
    '&.Mui-disabled': {
      display: 'none',
    },
    alignSelf: 'flex-start',
  },
  savedViewDeleteButton: {
    display: 'flex',
    gap: 8,
  },
  toggleTxt: {
    fontSize: '14px',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline-block',
  },
  headerButtonGroup: {
    height: '48px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '12px 18px',
    flexDirection: 'column',
    gap: 10,
    background: colors.white,
  },
  fieldWrapper: {
    border: `1px solid ${colors.black10}`,
    padding: '18px 20px',
    borderRadius: 5,
  },
  fieldWrapperError: {
    borderColor: colors.pureRed,
  },
  searchContainer: {
    zIndex: 10,
    padding: '10px 20px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
  searchbar: {
    padding: '8px 12px',
    backgroundColor: colors.cultured7,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 'normal',
    color: colors.black0,
    borderRadius: 4,
    '&::placeholder': {
      color: colors.black35,
    },
    width: '100%',
    '& .MuiInputBase-input': {
      padding: '2px 0',
    },
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      fontSize: 16,
    },
  },
  searchIcon: {
    color: colors.black35,
    height: 22,
    width: 22,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '17px 18px 17px 10px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    justifyContent: 'space-between',
    gap: 5,
  },
  rowLabelContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    margin: 0,
    padding: 0,
  },
  rowLabelPrimary: {
    flex: 'auto',
    fontSize: '0.875rem',
    lineHeight: 'normal',
  },
  rowLabelSecondary: {
    flex: 'auto',
    fontSize: '0.75rem',
    lineHeight: 'normal',
  },
  rowLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 14,
    fontWeight: 400,
    flex: 'auto',
    color: colors.black0,
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    lineHeight: 'normal',
  },
  emptyText: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
    height: '100%',
    alignItems: 'center',
    paddingTop: '175px',
  },
  detailContainer: {
    padding: 0,
  },
  savedViewDeleteButtonIcon: {
    color: colors.danger,
  },
  savedViewDeleteButtonText: {
    color: colors.danger,
  },
  savedViewShowDetailButtonIcon: {
    color: colors.info,
  },
  actionInfoText: {
    color: colors.black54,
    fontWeight: 500,
  },
  actionWarningText: {
    color: colors.danger,
    fontWeight: 500,
  },
  dialog: {
    height: '90vh',
    maxHeight: '100%',
    width: '680px',
    maxWidth: '680px',
    borderRadius: 5,
    boxShadow: 'none',
    overflow: 'hidden',
    '@media (max-width: 650px)': {
      width: '100%',
      margin: 0,
      height: '100%',
      borderRadius: 0,
      maxWidth: '100% !important',
      maxHeight: '100% !important',
      minWidth: '100% !important',
    },
  },
  dialogWrapper: {
    height: '100%',
    overflow: 'hidden',
    paddingTop: 6,
    paddingBottom: 6,
    backgroundColor: colors.lotionWhite,
  },
  dialogContent: {
    maxHeight: 'calc(100% - 195px)',
    width: 'unset',
    gap: 10,
  },
  dialogHeader: {
    height: 21,
  },
  dialogButton: {
    textTransform: 'capitalize',
  },
  dialogCloseButton: {
    color: colors.black54,
  },
  dialogCancelButton: {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  dialogSubmitButton: {
    boxShadow: 'unset',
  },
  hidden: {
    display: 'none',
  },
}));

export default ViewstateComponent;
